import { AxiosError } from 'axios'
import { createStore } from './util'
import { useApp } from './app'
import { notify } from '@/utils'

// API Response format from /referral/stats - matches API types exactly
interface ReferralStatsAPIResponse {
  referralCode: string | null
  totalReferrals: number
  totalPointsEarned: number
  usageCount: number
  maxUses: number
  codeCreatedAt: string | null
}

// Browser extension compatible interfaces
export interface ReferralCodeInfo {
  code: string | null
  usageCount: number
  maxUses: number
  createdAt: string | null
}

export interface ReferralStats {
  referralCode: string | null
  totalReferrals: number
  totalPointsEarned: number
  usageCount: number
  maxUses: number
  codeCreatedAt: string | null
}

export interface ReferralHistoryItem {
  id: string
  pingingUserId: string // User who triggered the ping/referral
  rewardedUserId: string // User who received the points
  pointsRewarded: number // Points awarded
  level: number // Referral level (1, 2, 3)
  createdAt: string
  source: string // "referral" or "legacy"
}

export interface UseReferralCodeResponse {
  success: boolean
  message: string
  referredPoints?: number
  referrerPoints?: number
}

// Legacy compatibility - use ReferralHistoryItem instead
export type Referral = ReferralHistoryItem

interface ReferralStore {
  loading: boolean
  generating: boolean
  applying: boolean
  error: string | null
  myGeneratedCode: ReferralCodeInfo | null
  stats: ReferralStats | null
  history: ReferralHistoryItem[]

  // API methods
  fetchMyGeneratedCode: () => Promise<void>
  generateMyCode: () => Promise<void>
  generateMyReferralCode: () => Promise<void> // Keep for backward compatibility
  applyReferralCode: (referralCode: string, userId?: string) => Promise<UseReferralCodeResponse>
  getStats: () => Promise<void>
  getHistory: (startDate?: string, endDate?: string) => Promise<void>
  clearError: () => void
}

function extractErrorMessage(error: unknown): string {
  if (error instanceof AxiosError) {
    const responseMessage = (error.response?.data as { message?: string })?.message
    return responseMessage || error.message || 'Unexpected error'
  }
  return 'Unexpected error'
}

export const useReferral = createStore<ReferralStore>(
  'referral',
  (set, get) => ({
    loading: false,
    generating: false,
    applying: false,
    error: null,
    myGeneratedCode: null,
    stats: null,
    history: [],

    fetchMyGeneratedCode: async () => {
      const http = useApp.getState().http
      set({ loading: true })
      try {
        const response = await http.get<ReferralCodeInfo>('/referral/my-code')
        set({ myGeneratedCode: response.data })
      } catch (error) {
        if (error instanceof AxiosError && error.response?.status === 404) {
          set({
            myGeneratedCode: { code: null, usageCount: 0, maxUses: 0, createdAt: null },
          })
        } else {
          const errorMsg = extractErrorMessage(error)
          set({ error: errorMsg })
          console.error('Failed to load referral code', error)
        }
      } finally {
        set({ loading: false })
      }
    },

    generateMyCode: async () => {
      const http = useApp.getState().http
      set({ generating: true, error: null })
      try {
        const response = await http.post<ReferralCodeInfo>('/referral/generate', {})
        set({ myGeneratedCode: response.data })
        // Also refresh stats
        await get().getStats()
        notify.success('Your referral code generated successfully!')
      } catch (error) {
        const errorMsg = extractErrorMessage(error)
        set({ error: errorMsg })
        console.error('Failed to generate referral code:', error)
        notify.error('Failed to generate your referral code')
      } finally {
        set({ generating: false })
      }
    },

    generateMyReferralCode: async () => {
      // Backward compatibility - delegate to generateMyCode
      await get().generateMyCode()
    },

    applyReferralCode: async (referralCode: string): Promise<UseReferralCodeResponse> => {
      const http = useApp.getState().http
      set({ applying: true, error: null })
      try {
        const response = await http.post<UseReferralCodeResponse>('/referral/use', {
          referralCode,
        })

        return response.data
      } catch (error) {
        const errorMsg = extractErrorMessage(error)
        set({ error: errorMsg })
        console.error(extractErrorMessage(error))
        return { success: false, message: errorMsg }
      } finally {
        set({ applying: false })
      }
    },

    getStats: async () => {
      const http = useApp.getState().http
      set({ loading: true })
      try {
        const response = await http.get<ReferralStatsAPIResponse>('/referral/stats')
        // Map API response to store format
        const mappedStats: ReferralStats = {
          referralCode: response.data.referralCode,
          totalReferrals: response.data.totalReferrals,
          totalPointsEarned: response.data.totalPointsEarned,
          usageCount: response.data.usageCount,
          maxUses: response.data.maxUses,
          codeCreatedAt: response.data.codeCreatedAt,
        }
        set({ stats: mappedStats })
      } catch (error) {
        const errorMsg = extractErrorMessage(error)
        set({ error: errorMsg })
        console.error(extractErrorMessage(error))
      } finally {
        set({ loading: false })
      }
    },

    getHistory: async (startDate?: string, endDate?: string) => {
      const http = useApp.getState().http
      set({ loading: true })
      try {
        const params = new URLSearchParams()
        if (startDate) params.append('start_date', startDate)
        if (endDate) params.append('end_date', endDate)

        const queryString = params.toString()
        const url = `/referral/history${queryString ? `?${queryString}` : ''}`

        const response = await http.get<{ history: ReferralHistoryItem[] }>(url)
        set({ history: response.data.history || [] })
      } catch (error) {
        const errorMsg = extractErrorMessage(error)
        set({ error: errorMsg })
        console.error(extractErrorMessage(error))
      } finally {
        set({ loading: false })
      }
    },

    clearError: () => {
      set({ error: null })
    },
  }),
  false,
)
